import { apiBaseUrl, buildApiHeaders } from './api.ts'

/**
 * The one HTTP error the admin panel throws. It lived in `hierarchy-api.ts`
 * purely because that was the first client written; every other client then
 * imported it from there and re-exported it, which made the hierarchy module
 * look like the owner of a concern it has nothing to do with. It belongs
 * here, next to the fetch that throws it. `hierarchy-api.ts` (and the rest)
 * still re-export it so no call site had to change.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'ApiError'
    // Some transpile targets break instanceof for Error subclasses without
    // an explicit prototype restore.
    Object.setPrototypeOf(this, ApiError.prototype)
  }
}

interface ValidationIssue {
  path?: (string | number)[]
  message?: string
}

/**
 * Turn an error response's body into the message a human reads.
 *
 * `validation_failed` bodies carry zod's `issues`; fold the field paths and
 * messages in so a form can say WHAT was wrong, not just that something was
 * (found live twice: "validation error" on a vault key with a space in it,
 * and a blank number field on an agent role showing only
 * "validation_failed"). Only `vault-api-core.ts` and `registry-api.ts` ever
 * did this unfolding — making it the shared default is the one deliberate
 * behaviour change in this refactor: every client's error messages get
 * better, none get worse.
 *
 * A body that is not JSON at all (a proxy's HTML error page) yields `null`,
 * and the caller falls back to a status-only message.
 */
export async function parseErrorBody(
  response: Response,
): Promise<string | null> {
  try {
    const body = (await response.json()) as {
      error?: string
      issues?: ValidationIssue[]
    }
    if (body.error === 'validation_failed' && body.issues?.length) {
      const details = body.issues
        .map((issue) => {
          const path = (issue.path ?? []).join('.')
          const message = issue.message ?? 'invalid'
          return path === '' ? message : `${path}: ${message}`
        })
        .join('; ')
      return `validation failed — ${details}`
    }
    return body.error ?? null
  } catch {
    return null
  }
}

/**
 * The admin panel's single API fetch.
 *
 * There were seven near-identical copies of this (`apiFetch`,
 * `sessionsFetch`, `vaultFetch`, `alertsFetch`, `registryFetch`,
 * `contentFetch`, `composioFetch`), differing only in how good their error
 * message was. A factory was considered and rejected: nothing varies between
 * the call sites — same base URL, same credentials, same error shape — so a
 * `createApiFetch(options)` would be a parameterless factory, which is just
 * this function with ceremony. If a second API origin ever appears, promote
 * it to a factory then (YAGNI until it is real).
 *
 * `credentials: 'include'` goes before the spread so an explicit `init`
 * could override it, and the headers go after so `buildApiHeaders` (which
 * already merged `init.headers`) always wins — that ordering is load-bearing
 * for SSR cookie forwarding.
 */
export async function apiFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    credentials: 'include',
    ...init,
    headers: await buildApiHeaders(init),
  })
  if (!response.ok) {
    const bodyError = await parseErrorBody(response)
    throw new ApiError(
      bodyError ?? `Request to ${path} failed with ${String(response.status)}`,
      response.status,
    )
  }
  // 204 No Content: the DELETE endpoints reply with no body at all.
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}
